import React, { useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { ALLERGY_SEVERITY_STYLES, getAllergySeverity } from "@/lib/clientFields";

/**
 * Searchable multi-select combobox.
 *
 * Props:
 * - value: string[]
 * - onChange(next: string[]): void
 * - options: (string | { value: string, severity?: "severe"|"moderate"|"mild" })[]
 * - placeholder: string
 * - allowCustom: bool  → type + enter adds a new tag
 * - variant: "default" | "allergy"  → allergy colour-codes badges by severity
 * - testid: string
 */
export default function SearchableMultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = "Search…",
  allowCustom = false,
  variant = "default",
  testid = "multi-select",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  const selected = Array.isArray(value) ? value : [];

  const normalised = useMemo(() => options.map((o) => (typeof o === "string" ? { value: o } : o)), [options]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return normalised.filter((o) => (q ? o.value.toLowerCase().includes(q) : true));
  }, [normalised, query]);

  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };

  const removeTag = (v) => onChange(selected.filter((x) => x !== v));

  const addCustom = () => {
    const t = query.trim();
    if (!t) return;
    if (selected.includes(t)) return;
    onChange([...selected, t]);
    setQuery("");
    inputRef.current?.focus();
  };

  const badgeClass = (item) => {
    if (variant === "allergy") {
      const sev = getAllergySeverity(item);
      return `${ALLERGY_SEVERITY_STYLES[sev]} border`;
    }
    return "bg-sky-50 text-sky-700 border-sky-200 border dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800";
  };

  const showCustomHint = allowCustom && query.trim() && !filtered.some((o) => o.value.toLowerCase() === query.trim().toLowerCase());

  return (
    <div className="space-y-1.5" data-testid={testid}>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((item) => (
            <span
              key={item}
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${badgeClass(item)}`}
              data-testid={`${testid}-tag-${item}`}
            >
              {item}
              <button
                type="button"
                onClick={() => removeTag(item)}
                className="opacity-70 hover:opacity-100"
                data-testid={`${testid}-remove-${item}`}
                aria-label={`Remove ${item}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testid}-trigger`}
            className="w-full h-9 flex items-center justify-between rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 text-sm text-left text-slate-600 dark:text-slate-300 hover:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/40"
          >
            <span className="truncate">
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="p-0 w-[--radix-popover-trigger-width] max-w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
          data-testid={`${testid}-popover`}
        >
          <Command shouldFilter={false} className="bg-transparent">
            <CommandInput
              ref={inputRef}
              placeholder="Type to search…"
              value={query}
              onValueChange={setQuery}
              data-testid={`${testid}-search`}
            />
            <CommandList className="max-h-64">
              <CommandEmpty className="py-3 text-center text-xs text-slate-500">
                No matches.
              </CommandEmpty>
              <CommandGroup>
                {filtered.map((o) => {
                  const on = selected.includes(o.value);
                  return (
                    <CommandItem
                      key={o.value}
                      value={o.value}
                      onSelect={() => toggle(o.value)}
                      data-testid={`${testid}-opt-${o.value}`}
                      className="flex items-center gap-2 text-sm"
                    >
                      <span
                        className={`w-4 h-4 rounded border flex items-center justify-center ${
                          on ? "bg-sky-600 border-sky-600 text-white" : "border-slate-300 dark:border-slate-600"
                        }`}
                      >
                        {on && <Check className="w-3 h-3" />}
                      </span>
                      <span className="flex-1">{o.value}</span>
                      {variant === "allergy" && o.severity && (
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${ALLERGY_SEVERITY_STYLES[o.severity]} border`}>
                          {o.severity}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
                {showCustomHint && (
                  <CommandItem
                    value={`__add:${query}`}
                    onSelect={addCustom}
                    data-testid={`${testid}-add-custom`}
                    className="flex items-center gap-2 text-sm text-sky-700 dark:text-sky-300"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add &quot;{query.trim()}&quot;
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
